/**
 * OneQrew Brand Design Tokens
 *
 * Vollständiger Brand Guide (OneQrew)
 * Alle Farben, Radien, Spacing und Typography-Definitionen
 * basierend auf dem offiziellen OneQrew Brand Styleguide
 */

// ============================================================================
// 1. BASIS-FARBEN (Primär, Sekundär & Akzent)
// ============================================================================

export const colors = {
  // Primärfarben
  primary: {
    purple: '#190023',    // RGB: 25/0/35
    white: '#FFFFFF',     // RGB: 255/255/255
  },

  // Sekundärfarbe
  secondary: {
    lightGrey: '#F4F3EE', // RGB: 244/243/238
  },

  // Aktivfarbe
  accent: {
    yellow: '#F0F55F',    // RGB: 240/245/95
  },

  // Purple-Abstufungen
  purpleShades: {
    light: '#5F4B64',
    medium: '#463250',
    dark: '#2D1937',
    darkest: '#190023',
  },

  // White/Light Grey-Abstufungen
  greyShades: {
    lightest: '#F4F3EE',
    light: '#E2E2D9',
    medium: '#C5C4B9',
  },
};

// ============================================================================
// 2. UI COLORSCHEME 1 (Hell / Light Theme)
// ============================================================================

export const colorSchemeLight = {
  // Primary Container (Main Button - Purple)
  primaryContainer: '#190023',
  onPrimaryContainer: '#FFFFFF',

  // Primary Container Alt (Main Button - Yellow)
  primaryContainerAlt: '#F0F55F',
  onPrimaryContainerAlt: '#190023',

  // Secondary Container (Outline/Ghost Button - White)
  secondaryContainer: '#FFFFFF',
  onSecondaryContainer: '#190023',

  // Tertiary Container (Light Grey)
  tertiaryContainer: '#F4F3EE',
  onTertiaryContainer: '#190023',

  // Surfaces
  primarySurface: '#FFFFFF',
  onPrimarySurface: '#190023',

  secondarySurface: '#F4F3EE',
  onSecondarySurface: '#606066',

  tertiarySurface: '#5F4B64',
  onTertiarySurface: '#FFFFFF',

  // Signal Colors
  error: '#D53F3F',
  onError: '#FFFFFF',

  warning: '#B05800',
  onWarning: '#FFFFFF',

  success: '#33852E',
  onSuccess: '#FFFFFF',

  // Disabled States
  disabledContainer: '#F4F3EE',
  onDisabledContainer: '#606066',
};

// ============================================================================
// 3. UI COLORSCHEME 2 (Dunkel / Dark Theme)
// ============================================================================

export const colorSchemeDark = {
  // Primary Container (Main Button - Yellow on Dark)
  primaryContainer: '#F0F55F',
  onPrimaryContainer: '#190023',

  // Primary Container Alt (Main Button - Purple on Dark)
  primaryContainerAlt: '#190023',
  onPrimaryContainerAlt: '#F0F55F',

  // Secondary Container (Outline/Ghost Button - Purple Shade)
  secondaryContainer: '#463250',
  onSecondaryContainer: '#FFFFFF',

  // Tertiary Container
  tertiaryContainer: '#5F4B64',
  onTertiaryContainer: '#FFFFFF',

  // Surfaces
  primarySurface: '#190023',
  onPrimarySurface: '#FFFFFF',

  secondarySurface: '#463250',
  onSecondarySurface: '#FFFFFF',

  tertiarySurface: '#574564', // Spezifischer Dark-Wert aus PDF
  onTertiarySurface: '#FFFFFF',

  // Signal Colors (gleich wie Light)
  error: '#D53F3F',
  onError: '#FFFFFF',

  warning: '#B05800',
  onWarning: '#FFFFFF',

  success: '#33852E',
  onSuccess: '#FFFFFF',

  // Disabled States
  disabledContainer: '#5F4B64',
  onDisabledContainer: '#95949A', // Outline Variant
};

// ============================================================================
// 4. ZUSTANDSFARBEN (States) & FOKUS
// ============================================================================

export const interactionStates = {
  // Fokus-Ring (für Keyboard Navigation)
  focussed: '#C534FF',
  focusRingWidth: '2px',
  focusRingOffset: '2px',

  // State Layers (als Overlay)
  stateLayer: {
    hovered: 'rgba(197, 196, 185, 0.20)',    // #C5C4B9 mit 20% Opacity
    selected: 'rgba(197, 196, 185, 0.25)',   // #C5C4B9 mit 25% Opacity
    pressed: 'rgba(197, 196, 185, 0.30)',    // #C5C4B9 mit 30% Opacity
  },
};

// ============================================================================
// 5. TYPOGRAPHIE
// ============================================================================

export const typography = {
  // Hausschrift
  fontFamily: {
    primary: '"Acid Grotesk OneQrew", Arial, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
    fallback: 'Arial, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
  },

  // Font Weights
  fontWeights: {
    book: 400,    // Acid Grotesk Book
    medium: 500,  // Acid Grotesk Medium
    bold: 700,    // Acid Grotesk Bold
  },

  // Desktop Text Styles
  desktop: {
    h1: {
      fontSize: '72px',
      fontWeight: 500, // Medium
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '54px',
      fontWeight: 500,
      lineHeight: 1.2,
    },
    h3: {
      fontSize: '36px',
      fontWeight: 500,
      lineHeight: 1.3,
    },
    h4: {
      fontSize: '24px',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    intro: {
      fontSize: '24px',
      fontWeight: 400, // Book
      lineHeight: 1.5,
    },
    body: {
      fontSize: '18px',
      fontWeight: 400,
      lineHeight: 1.6,
    },
  },

  // Mobile Text Styles
  mobile: {
    h1: {
      fontSize: '45px',
      fontWeight: 500,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '36px',
      fontWeight: 500,
      lineHeight: 1.2,
    },
    h3: {
      fontSize: '24px',
      fontWeight: 500,
      lineHeight: 1.3,
    },
    h4: {
      fontSize: '20px',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    intro: {
      fontSize: '20px',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    body: {
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: 1.6,
    },
  },
};

// ============================================================================
// 6. LAYOUT, RASTER & ABSTÄNDE (8px System)
// ============================================================================

export const spacing = {
  // 8px Grid System (alle Abstände sind Vielfache von 8)
  unit: 8,

  // Spacing Scale
  xs: 8,     // 1 unit
  sm: 16,    // 2 units
  md: 24,    // 3 units
  lg: 32,    // 4 units
  xl: 40,    // 5 units
  xxl: 48,   // 6 units
  xxxl: 64,  // 8 units
};

export const borderRadius = {
  // Radien (kritisch für OneQrew Design)
  xxs: '8px',
  s: '24px',
  m: '32px',
  xl: '64px',
  xxl: '80px',

  // Radius-Padding-Regel: Innenabstand = Eckenradius
  // Beispiel: Radius 32px → Padding 32px
};

// ============================================================================
// 7. ICONS
// ============================================================================

export const icons = {
  // Material Design Icons Konfiguration
  style: 'Rounded',
  weight: 300,
  grade: 0,
  opticalSize: 24,

  // Logo Mindesthöhe
  logoMinHeight: 24,
};

// ============================================================================
// 8. SCHATTEN & ELEVATIONS
// ============================================================================

export const shadows = {
  none: 'none',
  sm: '0px 2px 4px rgba(25, 0, 35, 0.08)',
  md: '0px 4px 8px rgba(25, 0, 35, 0.12)',
  lg: '0px 8px 16px rgba(25, 0, 35, 0.16)',
  xl: '0px 12px 24px rgba(25, 0, 35, 0.20)',
};

// ============================================================================
// 9. BREAKPOINTS (Responsive Design)
// ============================================================================

export const breakpoints = {
  xs: 0,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1920,
};

// ============================================================================
// EXPORT SUMMARY
// ============================================================================

const tokens = {
  colors,
  colorSchemeLight,
  colorSchemeDark,
  interactionStates,
  typography,
  spacing,
  borderRadius,
  icons,
  shadows,
  breakpoints,
};

export default tokens;
